import { useQuery } from '@tanstack/react-query'
import { getAnalyticsManagers, type AnalyticsPeriodParams } from '../../shared/api/analytics.api'
import { Spinner } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { useAnalyticsTable, type ColumnDef } from './shared/useAnalyticsTable'
import { SortableTh } from './shared/SortableTh'

/**
 * Вкладка «Менеджеры» модуля «Аналитика» (этап 4 docs/analytics-plan.md).
 * Сводная таблица: план/факт ₽ + %, АКБ, активная база, срывы, нагрузка.
 * Сортировка по всем колонкам + фильтр по менеджеру (клик по заголовку/воронке).
 */
const rub = (n: number): string => `${n.toLocaleString('ru-RU')} ₽`

const pctClass = (pct: number): string =>
  pct >= 100 ? 'text-emerald-600' : pct >= 70 ? 'text-amber-600' : 'text-red-600'

type AnalyticsManagerRow = {
  managerId: string
  name: string
  planAmount: number
  factAmount: number
  planPct: number
  akbPlanCount: number
  akbFactCount: number
  totalClients: number
  activeClients: number
  overdueCount: number
  activeLoad: number
}

const COLUMNS: ColumnDef<AnalyticsManagerRow>[] = [
  { key: 'name', sort: (r) => r.name.toLowerCase(), filter: (r) => r.name },
  { key: 'planAmount', sort: (r) => r.planAmount },
  { key: 'factAmount', sort: (r) => r.factAmount },
  { key: 'planPct', sort: (r) => r.planPct },
  // АКБ сортируем по факту — это полезный для РОПа сигнал.
  { key: 'akbFactCount', sort: (r) => r.akbFactCount },
  { key: 'totalClients', sort: (r) => r.totalClients },
  { key: 'activeClients', sort: (r) => r.activeClients },
  { key: 'overdueCount', sort: (r) => r.overdueCount },
  { key: 'activeLoad', sort: (r) => r.activeLoad },
]

export function ManagersTab({ periodParams }: { periodParams: AnalyticsPeriodParams }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-managers', periodParams],
    queryFn: () => getAnalyticsManagers(periodParams),
  })

  const table = useAnalyticsTable(
    (data?.rows ?? []) as AnalyticsManagerRow[],
    COLUMNS,
    undefined,
    'managers',
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (isError || !data) {
    return <ErrorMessage error="Не удалось загрузить аналитику по менеджерам" />
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900">Менеджеры — сводка за период</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        План/факт, АКБ (количество клиентов), активная клиентская база, срывы дедлайнов, нагрузка.
        Клик по заголовку — сортировка, воронка — фильтр.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs border-b border-gray-200">
              <SortableTh table={table} col="name" className="pr-3">
                Менеджер
              </SortableTh>
              <SortableTh table={table} col="planAmount" align="right" className="px-3">
                План ₽
              </SortableTh>
              <SortableTh table={table} col="factAmount" align="right" className="px-3">
                Факт ₽
              </SortableTh>
              <SortableTh table={table} col="planPct" align="right" className="px-3">
                %
              </SortableTh>
              <SortableTh table={table} col="akbFactCount" align="right" className="px-3">
                АКБ план/факт
              </SortableTh>
              <SortableTh table={table} col="totalClients" align="right" className="px-3">
                Клиентов
              </SortableTh>
              <SortableTh table={table} col="activeClients" align="right" className="px-3">
                Активных
              </SortableTh>
              <SortableTh table={table} col="overdueCount" align="right" className="px-3">
                Просрочки
              </SortableTh>
              <SortableTh table={table} col="activeLoad" align="right" className="pl-3">
                Нагрузка
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.view.map((r) => (
              <tr key={r.managerId} className="border-b border-gray-50">
                <td className="py-2 pr-3 font-medium text-gray-800">{r.name}</td>
                <td className="py-2 px-3 text-right text-gray-600">{rub(r.planAmount)}</td>
                <td className="py-2 px-3 text-right text-gray-800">{rub(r.factAmount)}</td>
                <td className={`py-2 px-3 text-right font-medium ${pctClass(r.planPct)}`}>
                  {r.planPct}%
                </td>
                <td className="py-2 px-3 text-right text-gray-600">
                  {r.akbPlanCount} / {r.akbFactCount}
                </td>
                <td className="py-2 px-3 text-right text-gray-600">{r.totalClients}</td>
                <td className="py-2 px-3 text-right text-gray-800">{r.activeClients}</td>
                <td
                  className={`py-2 px-3 text-right ${
                    r.overdueCount > 0 ? 'text-red-600 font-medium' : 'text-gray-400'
                  }`}
                >
                  {r.overdueCount}
                </td>
                <td className="py-2 pl-3 text-right text-gray-600">{r.activeLoad}</td>
              </tr>
            ))}
            {table.view.length === 0 && (
              <tr>
                <td colSpan={9} className="py-6 text-center text-sm text-gray-400">
                  Нет менеджеров в области видимости
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
